import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../../api/apiClient';
import type { FlowGroupId } from '../flow/groups';

export interface WalletMembership {
    group: string;
    admitted_at: string;
    admission_id: string;
}

export interface MarketWallet {
    address: string;
    alias: string | null;
    tags: string[];
    side: 'YES' | 'NO' | 'flat';
    net_shares: number;
    net_exposure_usd: number;
    realized_pnl: number;
    unrealized_pnl: number;
    total_pnl: number;
    trade_count: number;
    group_memberships: WalletMembership[];
    scores: Record<string, number>;
}

interface MarketWalletsResponse {
    market_id: string;
    at_ts: string;
    current_price_at_ts: number | null;
    groups_at_ts: Record<string, number>;
    total?: number;
    traders: MarketWallet[];
}

export type WalletTab = FlowGroupId | 'all';

export type WalletSort = 'exposure' | 'pnl';

export interface MarketWalletsResult {
    wallets: MarketWallet[];
    total: number | null;
    hasMore: boolean;
    groupCounts: Record<string, number> | null;
    loading: boolean;
    loadingMore: boolean;
    error: boolean;
    loadedTab: WalletTab | null;
    reload: () => void;
    loadMore: () => void;
}

export const WALLET_PAGE_SIZE = 10;
const REQUEST_TIMEOUT_MS = 25_000;

export function mergePage(
    current: readonly MarketWallet[],
    page: readonly MarketWallet[]
): { wallets: MarketWallet[]; added: number } {
    const seen = new Set(current.map(w => w.address));
    const fresh = page.filter(w => !seen.has(w.address));
    return { wallets: [...current, ...fresh], added: fresh.length };
}

export function useMarketWallets(
    marketId: string | null,
    tab: WalletTab,
    sort: WalletSort = 'exposure'
): MarketWalletsResult {
    const [wallets, setWallets] = useState<MarketWallet[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [groupCounts, setGroupCounts] = useState<Record<
        string,
        number
    > | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(false);
    const [loadedTab, setLoadedTab] = useState<WalletTab | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const reload = useCallback(() => setReloadKey(k => k + 1), []);

    const requestId = useRef(0);
    const walletsRef = useRef<MarketWallet[]>([]);
    walletsRef.current = wallets;

    const fetchPage = useCallback(
        (offset: number, signal: AbortSignal) => {
            const params = new URLSearchParams({
                sort,
                limit: String(WALLET_PAGE_SIZE),
                offset: String(offset),
            });
            if (tab !== 'all') params.set('group', tab);
            return apiGet<MarketWalletsResponse>(
                `/api/markets/${encodeURIComponent(marketId ?? '')}/traders?${params}`,
                signal
            );
        },
        [marketId, tab, sort]
    );

    useEffect(() => {
        setGroupCounts(null);
    }, [marketId]);

    useEffect(() => {
        const id = ++requestId.current;
        setWallets([]);
        setTotal(null);
        setHasMore(false);
        setError(false);
        setLoadedTab(null);
        setLoadingMore(false);
        if (!marketId) {
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        let timedOut = false;
        const timer = setTimeout(() => {
            timedOut = true;
            controller.abort();
            setError(true);
            setLoading(false);
        }, REQUEST_TIMEOUT_MS);
        setLoading(true);
        fetchPage(0, controller.signal)
            .then(res => {
                if (id !== requestId.current) return;
                clearTimeout(timer);
                const page = res.traders ?? [];
                setWallets(page);
                setTotal(typeof res.total === 'number' ? res.total : null);
                setHasMore(
                    typeof res.total === 'number'
                        ? page.length < res.total
                        : page.length === WALLET_PAGE_SIZE
                );
                setGroupCounts(res.groups_at_ts ?? {});
                setLoadedTab(tab);
                setLoading(false);
            })
            .catch(err => {
                if (timedOut || id !== requestId.current) return;
                if (controller.signal.aborted || err?.name === 'AbortError')
                    return;
                clearTimeout(timer);
                console.error('market wallets failed:', err);
                setError(true);
                setLoading(false);
            });
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [marketId, tab, fetchPage, reloadKey]);

    const loadMore = useCallback(() => {
        if (!marketId || loadingMore) return;
        const id = requestId.current;
        const controller = new AbortController();
        setLoadingMore(true);
        fetchPage(walletsRef.current.length, controller.signal)
            .then(res => {
                if (id !== requestId.current) return;
                const merged = mergePage(walletsRef.current, res.traders ?? []);
                setWallets(merged.wallets);
                if (typeof res.total === 'number') setTotal(res.total);
                setHasMore(
                    merged.added > 0 &&
                        (typeof res.total === 'number'
                            ? merged.wallets.length < res.total
                            : (res.traders ?? []).length === WALLET_PAGE_SIZE)
                );
                setLoadingMore(false);
            })
            .catch(err => {
                if (id !== requestId.current) return;
                console.error('more market wallets failed:', err);
                setLoadingMore(false);
            });
    }, [marketId, loadingMore, fetchPage]);

    return {
        wallets,
        total,
        hasMore,
        groupCounts,
        loading,
        loadingMore,
        error,
        loadedTab,
        reload,
        loadMore,
    };
}
